/**
 * 🏠 **첫 화면 무대 배치** — 좁은 화면에서 캐릭터가 한쪽에 몰리지 않게.
 *
 * ## 왜
 * 무대 위 캐릭터 자리는 **무대 폭의 비율(0~1)** 로 저장된다. 그래서 화면이 좁아져도 서로의
 * 상대 위치가 그대로다 — 넓은 화면에서 왼쪽에 나란히 둔 고양이·병아리가, 폰에서는 **왼쪽에
 * 겹쳐 몰리고 오른쪽은 텅 빈다.**
 *
 * 좁은 화면에서는 **병아리를 히어로 오른쪽으로 넘긴다**(고양이 · 히어로 · 병아리).
 * 넘기되 **가운데에서 떨어진 거리는 그대로** 둔다 — 운영자가 정한 간격을 존중한다.
 *
 * ## 이미 오른쪽에 둔 경우는 손대지 않는다
 * 운영자가 넓은 화면에서 병아리를 오른쪽에 배치했다면 좁은 화면에서도 오른쪽이다.
 * 무조건 뒤집으면 **운영자가 옮긴 대로 안 나오는** 화면이 된다.
 *
 * ## 관리자 미리보기는 넓은 화면 배치를 보여준다
 * 드래그로 자리를 정하는 화면(`ContextPreview`)은 넓은 폭이라 이 규칙이 안 걸린다 —
 * 거기서 정한 값이 **넓은 화면의 기준**이고, 좁은 화면은 그 값에서 이 규칙으로 파생된다.
 */

/** 이 폭 이하를 「좁은 화면」으로 본다(폰 세로). 전자책 펼침 기준(700)과 달리 무대는 조금 더 좁게. */
export const LANDING_NARROW_MAX = 640;

/** 무대 가운데 = 히어로가 서는 자리. */
export const LANDING_CENTER_X = 0.5;

export interface StagePos {
  x: number;
  y: number;
}

/**
 * 좁은 화면에서 쓸 자리. 넓은 화면이면 받은 값 그대로.
 *
 * 순서가 중요하다:
 * 1. **운영자가 폰 자리를 정했으면 그게 이긴다**(관리자 [🏠 홈 화면] → 「📱 좁은 화면」).
 *    사람이 직접 옮긴 것을 코드가 다시 옮기면 "옮긴 대로 안 나온다"가 된다
 * 2. 안 정했으면 기본 규칙 — `chick` 만 히어로 오른쪽으로 넘긴다. 고양이는 왼쪽에 남겨야
 *    히어로를 사이에 두고 좌우로 갈린다. 운영자가 추가한 캐릭터는 건드리지 않는다
 *    (어디에 뒀는지 우리가 알 수 없다)
 */
/**
 * 🔴 **자리를 안 정했을 때 어디에 서는가.**
 *
 * 운영자가 캐릭터 그림만 올리고 자리는 안 정하는 것이 보통이다(그게 첫 걸음이다).
 * 그때 쓰는 기본값이 **아이 화면과 관리자 미리보기에 따로** 적혀 있었다 —
 * 아이 화면은 `greg` 0.4/0.74 · `chick` 0.56/0.8 · 나머지 0.5/0.86,
 * 미리보기는 전부 0.45/0.72. 그래서 새로 올린 그림이 **미리보기와 실제 화면에서 다른 자리**에
 * 섰다. 운영자는 멀쩡한 것을 고치려 들거나 기능이 고장 났다고 여긴다.
 * (좁은 화면 규칙은 이미 이 파일로 모았는데, 기본 자리는 남아 있었다.)
 */
export const LANDING_CHAR_HOME: Record<string, StagePos> = {
  greg: { x: 0.4, y: 0.74 }, // 메인 왼쪽(책 읽는 검은 고양이)
  chick: { x: 0.56, y: 0.8 }, // 메인 오른쪽(병아리)
};

/** 운영자가 새로 올린 캐릭터 — 무대 아래 가운데에서 시작해 드래그로 옮긴다. */
export const LANDING_CHAR_HOME_EXTRA: StagePos = { x: 0.5, y: 0.86 };

/** 아이 자신의 히어로 — 무대 한가운데 주인공(마스코트가 양옆 아래). */
export const LANDING_HERO_HOME: StagePos = { x: 0.5, y: 0.66 };

/** 배경은 화면을 꽉 채운다 — 가운데. */
export const LANDING_BG_HOME: StagePos = { x: 0.5, y: 0.5 };

/** 이 캐릭터가 기본으로 서는 자리. 아이 화면·미리보기가 **같은 답**을 봐야 한다. */
export function landingCharHome(id: string): StagePos {
  return LANDING_CHAR_HOME[id] ?? LANDING_CHAR_HOME_EXTRA;
}

export function stagePosForWidth(
  id: string,
  pos: StagePos,
  narrow: boolean,
  posNarrow?: StagePos,
): StagePos {
  if (!narrow) return pos;
  if (posNarrow) return posNarrow; // 운영자가 직접 정한 자리
  if (id !== "chick") return pos;
  if (pos.x >= LANDING_CENTER_X) return pos; // 이미 오른쪽 — 그대로
  return { ...pos, x: LANDING_CENTER_X + (LANDING_CENTER_X - pos.x) };
}
